// Админ › Градове и райони — the cities table, the form for one, and its areas underneath.

import { useState } from 'react';
import { AreasTable } from './AreasTable.js';
import { CityForm, EMPTY_CITY } from './CityForm.js';
import { useCities, type CityRow } from './useCities.js';

function CitiesTable({
  cities,
  selected,
  onSelect,
  onEdit,
}: {
  cities: CityRow[];
  selected: string | null;
  onSelect: (key: string) => void;
  onEdit: (key: string) => void;
}) {
  return (
    <div className="adm__tablewrap">
      <table className="adm__table">
        <thead>
          <tr>
            <th>град</th>
            <th>ключ · slug</th>
            <th>латиница</th>
            <th>други изписвания</th>
            <th>център</th>
            <th className="adm__num">радиус</th>
            <th>активен</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {cities.map((city) => (
            <tr
              key={city.key}
              className={`adm__row--pick ${selected === city.key ? 'adm__row--selected' : ''} ${
                city.active ? '' : 'adm__row--off'
              }`}
              onClick={() => onSelect(city.key)}
            >
              <td>
                <strong>{city.name_bg}</strong>
                <span className="adm__sub"> {city.name_en}</span>
              </td>
              <td>
                <code>{city.key}</code>
                {city.slug && city.slug !== city.key ? <code> · {city.slug}</code> : null}
              </td>
              <td>{city.name_lat ?? '—'}</td>
              <td className="adm__sub">{city.aliases.join(', ') || '—'}</td>
              <td className="adm__mono">
                {city.center_lat !== null && city.center_lng !== null
                  ? `${city.center_lat.toFixed(4)}, ${city.center_lng.toFixed(4)}`
                  : '—'}
              </td>
              <td className="adm__num">{city.radius_m ?? '—'}</td>
              <td>{city.active ? 'да' : 'не'}</td>
              <td>
                <button
                  type="button"
                  className="adm__json-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(city.key);
                  }}
                >
                  Редакция
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CitiesSection() {
  const { data: cities = [], isLoading, error } = useCities();
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | 'new' | null>(null);

  if (isLoading) return <p className="adm__msg">Зареждане на градовете…</p>;
  if (error) return <p className="adm__err">{(error as Error).message}</p>;

  const current = selected ?? cities[0]?.key ?? null;
  const editRow = editing === 'new' ? EMPTY_CITY : (cities.find((c) => c.key === editing) ?? null);

  if (editRow !== null) {
    return (
      <CityForm
        key={editing}
        city={editRow}
        isNew={editing === 'new'}
        onDone={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="adm__queries-wrap">
      <div className="adm__section-header">
        <h3 className="adm__h">Градове и райони</h3>
        <span className="adm__sub">
          Територията на търсенето: град с център и радиус, и районите, с които се строят заявките.
        </span>
      </div>
      <CitiesTable cities={cities} selected={current} onSelect={setSelected} onEdit={setEditing} />
      <div className="adm__actions">
        <button type="button" className="adm__json-btn" onClick={() => setEditing('new')}>
          + Нов град
        </button>
      </div>
      {current !== null ? <AreasTable key={current} cityKey={current} /> : null}
    </div>
  );
}
